// Dearborn Denim upsell recommendation engine — pure logic, no DOM.
// UMD guard so vitest (CommonJS) and the browser both load it.
(function (root, factory) {
  if (typeof module === 'object' && module.exports) { module.exports = factory(); }
  else { root.DDRecEngine = factory(); }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var WAIST_TO_ALPHA = [
    { min: 30, max: 32, alpha: 'S' },
    { min: 32, max: 34, alpha: 'M' },
    { min: 34, max: 38, alpha: 'L' },
    { min: 38, max: 42, alpha: 'XL' }
  ];

  var ALPHA_NAMES = {
    'S': 'S', 'SMALL': 'S',
    'M': 'M', 'MEDIUM': 'M',
    'L': 'L', 'LARGE': 'L',
    'XL': 'XL', 'X-LARGE': 'XL', 'XLARGE': 'XL',
    '2XL': '2XL', 'XXL': '2XL', '2X-LARGE': '2XL'
  };

  var DEFAULT_PRIORITIES = ['Belt', 'Jeans', 'T-Shirt', 'Button Down Shirt', 'Underwear'];

  // Button-downs are chest-sized (36–46); published mapping from the size chart.
  var ALPHA_TO_CHEST = {
    'S': { min: 36, max: 36 },
    'M': { min: 38, max: 40 },
    'L': { min: 42, max: 44 },
    'XL': { min: 46, max: 46 }
  };

  function alphaToChestRange(alpha) {
    return ALPHA_TO_CHEST[alpha] || null;
  }

  function normalizeAlpha(token) {
    if (!token) return null;
    return ALPHA_NAMES[String(token).trim().toUpperCase()] || null;
  }

  function waistToAlpha(waist) {
    if (waist == null) return null;
    if (waist < WAIST_TO_ALPHA[0].min) return WAIST_TO_ALPHA[0].alpha;
    var match = null;
    for (var i = 0; i < WAIST_TO_ALPHA.length; i++) {
      var row = WAIST_TO_ALPHA[i];
      if (waist >= row.min && waist <= row.max) match = row.alpha; // later rows win → boundary resolves larger
    }
    return match || WAIST_TO_ALPHA[WAIST_TO_ALPHA.length - 1].alpha;
  }

  function alphaToWaistRange(alpha) {
    for (var i = 0; i < WAIST_TO_ALPHA.length; i++) {
      if (WAIST_TO_ALPHA[i].alpha === alpha) return { min: WAIST_TO_ALPHA[i].min, max: WAIST_TO_ALPHA[i].max };
    }
    return null;
  }

  // sizeSystem: 'waist' (jeans "32 / 28", belts "34") or 'alpha' ("Black / Medium")
  function parseVariantTitle(title, sizeSystem) {
    var out = { waist: null, inseam: null, alpha: null };
    if (!title) return out;
    var parts = String(title).split('/').map(function (p) { return p.trim(); });
    if (sizeSystem === 'waist') {
      var w = parseInt(parts[0], 10);
      if (!isNaN(w)) out.waist = w;
      if (parts.length > 1) {
        var ins = parseInt(parts[1], 10);
        if (!isNaN(ins)) out.inseam = ins;
      }
    } else {
      for (var i = 0; i < parts.length; i++) {
        var a = normalizeAlpha(parts[i]);
        if (a) { out.alpha = a; break; }
      }
    }
    return out;
  }

  function inferSizes(cartItems) {
    var waist = null, inseam = null, alpha = null, gender = null;
    for (var i = 0; i < (cartItems || []).length; i++) { // oldest → newest; later wins
      var it = cartItems[i];
      if (it.waist != null) { waist = it.waist; inseam = (it.inseam != null) ? it.inseam : inseam; }
      if (it.alpha) alpha = it.alpha;
      if (it.gender) gender = it.gender;
    }
    return { waist: waist, inseam: inseam, alpha: alpha, gender: gender };
  }

  function pickVariant(product, sizes) {
    var i, v, parsed;
    if (product.sizeSystem === 'chest') {
      var chestAlpha = sizes.alpha || waistToAlpha(sizes.waist);
      if (!chestAlpha) return null;
      var chestRange = alphaToChestRange(chestAlpha);
      if (!chestRange) return null;
      var chestOpts = [];
      for (i = 0; i < product.variants.length; i++) {
        v = product.variants[i];
        if (v.qty <= 0) continue;
        // Titles vary: numeric-only ("38") or combined ("Small/40"). Alpha token wins when present.
        var alphaTok = parseVariantTitle(v.title, 'alpha').alpha;
        if (alphaTok) {
          if (alphaTok === chestAlpha) return { variantId: v.id, price: v.price, sizeOptions: null };
          continue;
        }
        var numTok = null;
        var chestParts = String(v.title).split('/');
        for (var cp = 0; cp < chestParts.length && numTok == null; cp++) {
          var n = parseInt(chestParts[cp], 10);
          if (!isNaN(n)) numTok = n;
        }
        if (numTok != null && numTok >= chestRange.min && numTok <= chestRange.max) {
          chestOpts.push({ variantId: v.id, label: v.title, price: v.price });
        }
      }
      if (!chestOpts.length) return null;
      if (chestOpts.length === 1) return { variantId: chestOpts[0].variantId, price: chestOpts[0].price, sizeOptions: null };
      return { variantId: null, price: product.price, sizeOptions: chestOpts };
    }
    if (product.sizeSystem === 'waist') {
      if (sizes.waist != null) {
        var options = [];
        for (i = 0; i < product.variants.length; i++) {
          v = product.variants[i];
          if (v.qty <= 0) continue;
          parsed = parseVariantTitle(v.title, 'waist');
          if (parsed.waist !== sizes.waist) continue;
          if (product.type === 'Jeans' && sizes.inseam != null) {
            if (parsed.inseam === sizes.inseam) return { variantId: v.id, price: v.price, sizeOptions: null };
          } else if (product.type === 'Jeans') {
            options.push({ variantId: v.id, label: v.title, price: v.price }); // inseam unknown → picker
          } else {
            return { variantId: v.id, price: v.price, sizeOptions: null }; // belt: exact waist only
          }
        }
        return options.length ? { variantId: null, price: product.price, sizeOptions: options } : null;
      }
      if (sizes.alpha) { // alpha→waist never auto-selects
        var range = alphaToWaistRange(sizes.alpha);
        if (!range) return null;
        var opts = [];
        for (i = 0; i < product.variants.length; i++) {
          v = product.variants[i];
          if (v.qty <= 0) continue;
          parsed = parseVariantTitle(v.title, 'waist');
          if (parsed.waist != null && parsed.waist >= range.min && parsed.waist <= range.max) {
            opts.push({ variantId: v.id, label: v.title, price: v.price });
          }
        }
        return opts.length ? { variantId: null, price: product.price, sizeOptions: opts } : null;
      }
      return null;
    }
    // alpha-sized product: primary = inferred size, plus one alternative size as a second
    // one-tap button (next size up when stocked, else next size down)
    var targetAlpha = sizes.alpha || waistToAlpha(sizes.waist);
    if (!targetAlpha) return null;
    var ORDER = ['S', 'M', 'L', 'XL', '2XL'];
    var firstInStock = function (alpha) {
      for (var k = 0; k < product.variants.length; k++) {
        var vv = product.variants[k];
        if (vv.qty <= 0) continue;
        if (parseVariantTitle(vv.title, 'alpha').alpha === alpha) return { variantId: vv.id, price: vv.price };
      }
      return null;
    };
    var primary = firstInStock(targetAlpha);
    if (!primary) return null;
    var idx = ORDER.indexOf(targetAlpha);
    var alt = null, altAlpha = null, j;
    for (j = idx + 1; j < ORDER.length && !alt; j++) { alt = firstInStock(ORDER[j]); if (alt) altAlpha = ORDER[j]; }
    for (j = idx - 1; j >= 0 && !alt; j--) { alt = firstInStock(ORDER[j]); if (alt) altAlpha = ORDER[j]; }
    var sizeButtons = null;
    if (alt) {
      sizeButtons = [
        { label: targetAlpha, variantId: primary.variantId, price: primary.price },
        { label: altAlpha, variantId: alt.variantId, price: alt.price }
      ];
    }
    return { variantId: primary.variantId, price: primary.price, sizeOptions: null, sizeButtons: sizeButtons };
  }

  function recommend(opts) {
    var cartItems = opts.cartItems || [];
    var catalog = opts.catalog || [];
    var maxRecs = opts.maxRecs || 3;
    var priorities = opts.priorities || DEFAULT_PRIORITIES;
    var sizes = inferSizes(cartItems);
    if (sizes.waist == null && !sizes.alpha) return [];

    var inCart = {}, cartWashes = {};
    cartItems.forEach(function (it) {
      inCart[String(it.productId)] = true;
      if (it.type === 'Jeans' && it.wash) cartWashes[it.wash] = true;
    });

    var recs = [];
    for (var p = 0; p < priorities.length && recs.length < maxRecs; p++) {
      var type = priorities[p];
      // Products tagged upsell-featured are always tried first within their category
      var pool = catalog.filter(function (x) { return x.type === type; });
      pool = pool.filter(function (x) { return x.featured; }).concat(pool.filter(function (x) { return !x.featured; }));
      for (var c = 0; c < pool.length; c++) {
        var prod = pool[c];
        if (inCart[String(prod.id)]) continue;
        if (sizes.gender && prod.gender && prod.gender !== sizes.gender) continue;
        if (type === 'Jeans' && prod.wash && cartWashes[prod.wash]) continue;
        var pick = pickVariant(prod, sizes);
        if (!pick) continue;
        recs.push({
          productId: prod.id, handle: prod.handle, title: prod.title, image: prod.image,
          type: type, price: pick.price != null ? pick.price : prod.price,
          variantId: pick.variantId, sizeOptions: pick.sizeOptions,
          sizeButtons: pick.sizeButtons || null
        });
        break; // one rec per category
      }
    }
    return recs;
  }

  function shippingProgress(totalCents, thresholdCents) {
    if (totalCents >= thresholdCents) return { remainingCents: 0, pct: 100, reached: true };
    return {
      remainingCents: thresholdCents - totalCents,
      pct: Math.floor((totalCents / thresholdCents) * 100),
      reached: false
    };
  }

  return {
    waistToAlpha: waistToAlpha,
    alphaToWaistRange: alphaToWaistRange,
    alphaToChestRange: alphaToChestRange,
    normalizeAlpha: normalizeAlpha,
    parseVariantTitle: parseVariantTitle,
    inferSizes: inferSizes,
    pickVariant: pickVariant,
    recommend: recommend,
    shippingProgress: shippingProgress
  };
});
